/**
 * `a11y` — small accessibility helpers shared across the widget's render
 * primitives (polst card, campaign player, brand feed).
 *
 * Everything here is framework-free and jsdom-friendly. Nothing touches
 * globals beyond the `Document` handed in, so tests remain deterministic.
 */

/**
 * Visually-hidden style applied to the live region. Mirrors Tailwind's
 * `sr-only` — the region is rendered off-screen but still read by
 * assistive tech.
 */
const SR_ONLY_STYLE =
  "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;";

export interface LiveRegionHandle {
  /** The DOM node; attach where appropriate. */
  el: HTMLElement;
  /** Announce a message. Consecutive identical messages still fire a
   *  re-announcement because the region is cleared + re-set on the
   *  next microtask. */
  announce(message: string): void;
  /** Remove the node from its parent. */
  destroy(): void;
}

/**
 * Create a polite, atomic, visually-hidden `<span>` suitable for
 * announcing short status updates (vote tallies, campaign step
 * transitions).
 */
export function createLiveRegion(doc: Document): LiveRegionHandle {
  const el = doc.createElement("span");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-atomic", "true");
  el.setAttribute("role", "status");
  el.setAttribute("data-polst-live-region", "");
  el.setAttribute("style", SR_ONLY_STYLE);

  let last = "";
  return {
    el,
    announce(message: string): void {
      const next = typeof message === "string" ? message : String(message);
      // Clearing first forces SR to re-read the same text if it was
      // already present — otherwise NVDA / VoiceOver suppress duplicates.
      if (next === last) {
        el.textContent = "";
        Promise.resolve().then(() => {
          el.textContent = next;
        });
      } else {
        el.textContent = next;
        last = next;
      }
    },
    destroy(): void {
      if (el.parentNode) el.parentNode.removeChild(el);
    },
  };
}

/**
 * `prefers-reduced-motion: reduce` probe. Safe in jsdom (falls back to
 * `false` when `matchMedia` is missing or throws).
 */
export function prefersReducedMotion(doc: Document): boolean {
  const view = doc.defaultView as unknown as {
    matchMedia?: (q: string) => { matches: boolean };
  } | null;
  if (!view?.matchMedia) return false;
  try {
    return !!view.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Standard focusable-element selector. Matches the WAI-ARIA Authoring
 * Practices guidance: button, link with href, form control, anything
 * with a non-negative tabindex, anything with `contenteditable`.
 */
export const FOCUSABLE_SELECTOR =
  [
    "button:not([disabled])",
    "a[href]",
    "input:not([disabled]):not([type=hidden])",
    "textarea:not([disabled])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
    "[contenteditable='true']",
  ].join(",");

/**
 * Collect focusable descendants of `container` in DOM order. Filters
 * out `[aria-hidden="true"]` subtrees and elements whose `tabindex` is
 * explicitly `-1`.
 */
export function getFocusable(container: Element): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    // Walk ancestors to see if any asserts aria-hidden.
    let cur: Element | null = el;
    while (cur && cur !== container) {
      if (cur.getAttribute("aria-hidden") === "true") return false;
      cur = cur.parentElement;
    }
    return true;
  });
}

/** Focus the first interactive element inside `container`. No-op if none. */
export function focusFirstInteractive(container: Element): HTMLElement | null {
  const list = getFocusable(container);
  const first = list[0] ?? null;
  if (first && typeof first.focus === "function") {
    try {
      first.focus();
    } catch {
      /* focus can throw in some edge cases; swallow */
    }
  }
  return first;
}

export interface FocusTrapOptions {
  /** Called when Escape is pressed inside the container. */
  onEscape?: () => void;
  /** Initial focus target. Defaults to `focusFirstInteractive(container)`. */
  initialFocus?: HTMLElement;
}

/**
 * Install a focus trap on `container`. Tab at the last focusable
 * wraps to the first; Shift+Tab at the first wraps to the last;
 * Escape (if `onEscape` is provided) calls the callback.
 *
 * Returns a teardown fn the caller invokes on close.
 */
export function trapFocus(
  container: HTMLElement,
  options: FocusTrapOptions = {},
): () => void {
  const doc = container.ownerDocument;
  if (!doc) return () => {};

  const initial =
    options.initialFocus ?? getFocusable(container)[0] ?? null;
  if (initial && typeof initial.focus === "function") {
    try {
      initial.focus();
    } catch {
      /* swallow */
    }
  }

  function onKeydown(evt: KeyboardEvent): void {
    if (evt.key === "Escape" && options.onEscape) {
      evt.stopPropagation();
      options.onEscape();
      return;
    }
    if (evt.key !== "Tab") return;
    const list = getFocusable(container);
    if (list.length === 0) {
      evt.preventDefault();
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    if (!first || !last) return;
    const active = doc.activeElement as HTMLElement | null;
    if (evt.shiftKey) {
      if (active === first || !container.contains(active)) {
        evt.preventDefault();
        try {
          last.focus();
        } catch {
          /* swallow */
        }
      }
    } else {
      if (active === last) {
        evt.preventDefault();
        try {
          first.focus();
        } catch {
          /* swallow */
        }
      }
    }
  }

  container.addEventListener("keydown", onKeydown, true);
  return () => {
    container.removeEventListener("keydown", onKeydown, true);
  };
}
